// Shared direction/cooldown badge used in card + detail stats.
// Swaps to COOLDOWN when cycle status is 'cooldown' and appends a
// "reopen in Xm" hint if cooldown_until is in the future.
// Swaps further to PAUSED when the session has AI-set paused_until
// in the future — takes priority over COOLDOWN since a paused session
// won't reopen even after cooldown expires.
import { useEffect, useState } from "react";
import type { WindCloseReason, WindCycleStatus, WindDirection } from "@/types/api";

interface SessionDirectionBadgeProps {
  direction: WindDirection;
  cycleStatus: WindCycleStatus | null | undefined;
  cooldownUntil?: string | null;
  closeReason?: WindCloseReason | null;
  pausedUntil?: string | null;
  /** Compact mode — drop the "reopen in Xm" sub-label when the main label
   * already tells the story (used in list cards where space is tight and
   * AI SKIP / COOLDOWN alone conveys the session state).
   */
  compact?: boolean;
}

/** "2m", "45s", "1h 3m" — null when already past. */
function formatRemaining(ms: number): string | null {
  if (ms <= 0) return null;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

export default function SessionDirectionBadge({
  direction,
  cycleStatus,
  cooldownUntil,
  closeReason,
  pausedUntil,
  compact = false,
}: SessionDirectionBadgeProps) {
  // Tick every 30s so countdown stays fresh (cooldown/pause countdowns).
  const [, rerender] = useState(0);
  const isPaused = !!pausedUntil && new Date(pausedUntil).getTime() > Date.now();
  useEffect(() => {
    if (cycleStatus !== "cooldown" && !isPaused) return;
    const id = setInterval(() => rerender(n => n + 1), 30_000);
    return () => clearInterval(id);
  }, [cycleStatus, isPaused]);

  // AI-paused takes priority over cooldown — a paused session won't reopen
  // even after cooldown expires, so showing "reopen in Xm" would mislead.
  if (isPaused) {
    const remaining = formatRemaining(new Date(pausedUntil!).getTime() - Date.now());
    return (
      <span
        className="inline-flex items-center text-[0.7rem] font-medium px-1.5 py-0.5 rounded gap-1 whitespace-nowrap"
        style={{ background: "#a855f720", color: "#a855f7" }}
        title={`AI paused this session${remaining ? ` for ${remaining}` : ""} — regime flagged hostile`}
      >
        <span>AI PAUSED</span>
        {!compact && remaining && <span className="text-[0.65rem] opacity-80 whitespace-nowrap">{remaining}</span>}
      </span>
    );
  }

  if (cycleStatus === "cooldown") {
    const remaining = cooldownUntil
      ? formatRemaining(new Date(cooldownUntil).getTime() - Date.now())
      : null;
    const aiSkip = closeReason === "ai-skip";
    const label = aiSkip ? "AI SKIP" : "COOLDOWN";
    // Title is slightly different so the reason is obvious on hover.
    const title = aiSkip
      ? `AI advisor declined this cycle${remaining ? `; retry in ${remaining}` : ""}`
      : `Waiting before next cycle${remaining ? ` (${remaining})` : ""}`;
    return (
      <span
        className="inline-flex items-center text-[0.7rem] font-medium px-1.5 py-0.5 rounded gap-1 whitespace-nowrap"
        style={{ background: "#3b82f620", color: "#3b82f6" }}
        title={title}
      >
        <span>{label}</span>
        {!compact && remaining && (
          <span className="text-[0.65rem] opacity-80 whitespace-nowrap">reopen in {remaining}</span>
        )}
      </span>
    );
  }
  const isLong = direction === "long";
  const hex = isLong ? "#22c55e" : "#ef4444";
  return (
    <span
      className="inline-flex items-center text-[0.7rem] font-medium px-1.5 py-0.5 rounded"
      style={{ background: `${hex}20`, color: hex }}
    >
      {isLong ? "LONG" : "SHORT"}
    </span>
  );
}
